import type { JSX } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

/**
 * Quarry, by HabileLabs.
 *
 * The name does the same double duty the product does: a quarry is both the
 * thing you are looking for and the place you cut away everything else to reach
 * it. That is this product exactly — a list of companies goes in, most of what
 * is found is rejected by the gates, and what comes out is a small number of
 * people somebody can actually write to, with a record of what each one cost and
 * where it came from.
 *
 * The mark is a quarry in cross-section: three benches stepping down and one
 * solid dot at the bottom of the cut. Only the dot is filled, which is the whole
 * idea. It is drawn as a glyph rather than a picture so it survives 16px in a
 * browser tab, and it carries the HabileLabs gradient so the family resemblance
 * is obvious without repeating the parent mark.
 *
 * The name is set in text rather than baked into the image, so it stays
 * selectable, searchable and translatable, and so the lockup reflows on a phone.
 */

// Declared in @profiler/shared so the workers that write the product's name
// into exported workbooks read the same constant the browser does.
export { PARENT_NAME, PRODUCT_FULL_NAME, PRODUCT_NAME } from '@profiler/shared'
import { PARENT_NAME, PRODUCT_NAME } from '@profiler/shared'
/** One line, used in the page description, the sign-in card and empty states. */
export const PRODUCT_TAGLINE = 'Decision-makers at your target companies, with receipts.'

export const MARK_SRC = '/brand/quarry-mark.svg'
/** The parent company mark, for the one place that credits the parent directly. */
export const PARENT_MARK_SRC = '/brand/habilelabs-mark.svg'

export function BrandMark({ size = 28 }: Readonly<{ size?: number }>): JSX.Element {
  return (
    <Box
      component="img"
      src={MARK_SRC}
      alt=""
      aria-hidden
      sx={{ width: size, height: size, display: 'block', flexShrink: 0 }}
    />
  )
}

/**
 * Mark plus name plus parent.
 *
 * `compact` drops the parent line on narrow screens, where the nav needs the
 * width more than the attribution does — the parent is still credited on the
 * sign-in screen and in the account menu, which is where somebody actually asks
 * whose tool this is.
 */
export function BrandLockup({
  size = 28,
  compact = false,
}: Readonly<{
  size?: number
  compact?: boolean
}>): JSX.Element {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      <BrandMark size={size} />
      <Stack sx={{ minWidth: 0, lineHeight: 1.1 }}>
        <Typography
          component="span"
          sx={{
            fontSize: size * 0.62,
            fontWeight: 650,
            letterSpacing: '-0.015em',
            whiteSpace: 'nowrap',
            color: 'text.primary',
          }}
        >
          {PRODUCT_NAME}
        </Typography>
        {compact ? null : (
          <Typography
            component="span"
            sx={{
              fontSize: size * 0.36,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontWeight: 500,
            }}
          >
            by {PARENT_NAME}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}
